import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Button,
  BottomNavigation,
  BottomNavigationAction,
  Drawer,
  IconButton,
} from '@mui/material';
import { Logout as LogoutIcon, Menu as MenuIcon, Close as CloseIcon } from '@mui/icons-material';
import { useUser } from '../context/UserContext';
import urbanImage from '../assets/urban_logo.png';
import {
  ADMIN_NAV,
  CONSIGNOR_NAV,
  MOBILE_BOTTOM_BAR_COUNT_ADMIN,
  type SidebarNavItem,
} from '../navigation/sidebarNav';
import { fetchQueueBadges, type QueueBadges } from '../api/queueBadges';
import {
  ADMIN_CLEANING_QUEUE_PATH,
  ADMIN_HOME_PATH,
  ADMIN_INVENTORY_PATH,
  ADMIN_INVENTORY_RECOGIDA_STATUS,
  ADMIN_PHOTO_QUEUE_PATH,
} from '../routes/routePaths';

const SIDEBAR_WIDTH = 256;
const BOTTOM_NAV_HEIGHT = 56;

function pathnameSearchParams(search: string): URLSearchParams {
  return new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
}

/**
 * Ítem con `path` opcionalmente con query (`/ruta?a=b`). Estado activo coherente con la URL actual.
 */
function isNavActive(pathname: string, search: string, itemPath: string): boolean {
  const qIdx = itemPath.indexOf('?');
  const itemPathname = qIdx >= 0 ? itemPath.slice(0, qIdx) : itemPath;
  const itemQuery = qIdx >= 0 ? itemPath.slice(qIdx + 1) : null;

  if (itemQuery !== null) {
    if (pathname !== itemPathname) return false;
    const want = new URLSearchParams(itemQuery);
    const have = pathnameSearchParams(search);
    return Array.from(want.keys()).every((key) => have.get(key) === want.get(key));
  }

  if (itemPathname === '/home' || itemPathname === ADMIN_HOME_PATH) {
    return pathname === itemPathname;
  }

  const pathMatchesExact = pathname === itemPathname;
  const pathMatchesChild = pathname.startsWith(`${itemPathname}/`);

  if (pathMatchesExact) {
    if (
      itemPathname === ADMIN_INVENTORY_PATH &&
      itemPath === ADMIN_INVENTORY_PATH &&
      pathnameSearchParams(search).get('status') === ADMIN_INVENTORY_RECOGIDA_STATUS
    ) {
      return false;
    }
    return true;
  }

  return pathMatchesChild;
}

function SidebarNavContent({
  user,
  logout,
  items,
  onItemClick,
}: {
  user: { fullName?: string } | null;
  logout: () => void;
  items: SidebarNavItem[];
  onItemClick?: () => void;
}): React.ReactElement {
  const location = useLocation();

  return (
    <>
      <Box
        sx={{
          p: 3,
          borderBottom: '1px solid',
          borderColor: 'rgba(226, 232, 240, 1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <img
          src={urbanImage}
          alt="Urban Utility Logo"
          style={{ width: '80%', objectFit: 'contain', opacity: 0.9 }}
        />
      </Box>
      <Box sx={{ px: 2, py: 2, borderBottom: '1px solid', borderColor: 'rgba(226, 232, 240, 1)' }}>
        <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
          Bienvenido de vuelta
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
          {user?.fullName ?? 'Usuario'}
        </Typography>
      </Box>
      <Box component="nav" sx={{ flex: 1, px: 2, py: 3, overflow: 'auto' }}>
        <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {items.map((item) => {
            const selected = isNavActive(location.pathname, location.search, item.path);
            return (
              <ListItemButton
                key={item.path}
                component={Link}
                to={item.path}
                selected={selected}
                onClick={onItemClick}
                sx={{
                  borderRadius: 1,
                  py: 1.5,
                  '&.Mui-selected': {
                    bgcolor: 'rgba(241, 245, 249, 1)',
                    color: 'text.primary',
                    '&:hover': { bgcolor: 'rgba(241, 245, 249, 1)' },
                  },
                  '&:hover': {
                    bgcolor: 'rgba(248, 250, 252, 1)',
                    color: 'text.primary',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <item.icon sx={{ fontSize: 20, color: 'inherit' }} />
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }}
                />
                {item.badge != null && (
                  <Typography
                    component="span"
                    sx={{
                      ml: 'auto',
                      fontSize: 10,
                      fontWeight: 500,
                      bgcolor: 'rgba(226, 232, 240, 1)',
                      color: 'text.secondary',
                      px: 1,
                      py: 0.25,
                      borderRadius: 9999,
                    }}
                  >
                    {item.badge}
                  </Typography>
                )}
              </ListItemButton>
            );
          })}
        </List>
      </Box>
      <Box
        sx={{
          p: 2,
          borderTop: '1px solid',
          borderColor: 'rgba(226, 232, 240, 1)',
        }}
      >
        <Button
          fullWidth
          startIcon={<LogoutIcon sx={{ fontSize: 20 }} />}
          onClick={logout}
          sx={{
            justifyContent: 'flex-start',
            px: 1.5,
            py: 1.25,
            fontSize: 14,
            fontWeight: 500,
            color: 'text.secondary',
            textTransform: 'none',
            '&:hover': {
              bgcolor: 'rgba(248, 250, 252, 1)',
              color: 'text.primary',
            },
          }}
        >
          Cerrar sesión
        </Button>
      </Box>
    </>
  );
}

SidebarNavContent.defaultProps = {
  onItemClick: undefined,
};

function BottomNavBadge({ count }: { count: number }): React.ReactElement {
  return (
    <Typography
      component="span"
      sx={{
        position: 'absolute',
        top: -4,
        right: -8,
        fontSize: 10,
        fontWeight: 600,
        bgcolor: 'primary.main',
        color: 'white',
        minWidth: 16,
        height: 16,
        borderRadius: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {count}
    </Typography>
  );
}

function applyBadges(items: SidebarNavItem[], badges: QueueBadges | null): SidebarNavItem[] {
  if (!badges) return items;
  return items.map((item) => {
    if (item.path === ADMIN_PHOTO_QUEUE_PATH) {
      return { ...item, badge: badges.photoQueuePending };
    }
    if (item.path === ADMIN_CLEANING_QUEUE_PATH) {
      return { ...item, badge: badges.cleaningQueuePending };
    }
    return item;
  });
}

function Sidebar(): React.ReactElement {
  const { user, logout } = useUser();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [badges, setBadges] = useState<QueueBadges | null>(null);
  const location = useLocation();
  const isAdmin = Boolean(user?.isAdmin);
  const baseNavItems = isAdmin ? ADMIN_NAV : CONSIGNOR_NAV;
  const navItems = isAdmin ? applyBadges(baseNavItems, badges) : baseNavItems;

  useEffect(() => {
    if (!isAdmin) return;
    fetchQueueBadges().then((result) => {
      if (result.ok) setBadges(result.data);
    });
  }, [isAdmin, location.pathname]);

  const adminMobilePrimary = navItems.slice(0, MOBILE_BOTTOM_BAR_COUNT_ADMIN);
  const adminMobileOverflow = navItems.slice(MOBILE_BOTTOM_BAR_COUNT_ADMIN);
  let bottomNavValue: string | false = false;
  const primaryMatchPath = adminMobilePrimary.find((i) =>
    isNavActive(location.pathname, location.search, i.path)
  )?.path;
  if (primaryMatchPath !== undefined) {
    bottomNavValue = primaryMatchPath;
  } else if (
    isAdmin &&
    adminMobileOverflow.some((i) => isNavActive(location.pathname, location.search, i.path))
  ) {
    bottomNavValue = '__more__';
  }

  return (
    <>
      <Box
        component="aside"
        sx={{
          width: SIDEBAR_WIDTH,
          flexShrink: 0,
          borderRight: '1px solid',
          borderColor: 'rgba(226, 232, 240, 1)',
          bgcolor: 'white',
          display: { xs: 'none', lg: 'flex' },
          flexDirection: 'column',
          position: 'sticky',
          top: 0,
          height: '100vh',
        }}
      >
        <SidebarNavContent user={user} logout={logout} items={navItems} />
      </Box>

      {/* Mobile bottom navigation */}
      <BottomNavigation
        showLabels
        value={bottomNavValue}
        sx={{
          display: { xs: 'flex', lg: 'none' },
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          height: BOTTOM_NAV_HEIGHT,
          borderTop: '1px solid',
          borderColor: 'rgba(226, 232, 240, 1)',
          bgcolor: 'white',
          zIndex: 1100,
        }}
      >
        {isAdmin ? (
          <>
            {adminMobilePrimary.map((item) => (
              <BottomNavigationAction
                key={item.path}
                label={item.label}
                value={item.path}
                icon={
                  item.badge != null ? (
                    <Box sx={{ position: 'relative' }}>
                      <item.icon />
                      <BottomNavBadge count={item.badge} />
                    </Box>
                  ) : (
                    <item.icon />
                  )
                }
                component={Link}
                to={item.path}
              />
            ))}
            <BottomNavigationAction
              label="Más"
              value="__more__"
              icon={<MenuIcon />}
              component="button"
              onClick={() => setDrawerOpen(true)}
            />
          </>
        ) : (
          navItems.map((item) => (
            <BottomNavigationAction
              key={item.path}
              label={item.label}
              value={item.path}
              icon={<item.icon />}
              component={Link}
              to={item.path}
            />
          ))
        )}
      </BottomNavigation>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{ display: { xs: 'block', lg: 'none' } }}
        PaperProps={{
          sx: { width: SIDEBAR_WIDTH + 24, maxWidth: '100%' },
        }}
      >
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            pt: 2,
          }}
        >
          <Box sx={{ px: 2, pb: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <IconButton aria-label="Cerrar menú" onClick={() => setDrawerOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          {isAdmin && (
            <SidebarNavContent
              user={user}
              logout={logout}
              items={adminMobileOverflow}
              onItemClick={() => setDrawerOpen(false)}
            />
          )}
        </Box>
      </Drawer>
    </>
  );
}

export default Sidebar;
export { SIDEBAR_WIDTH, BOTTOM_NAV_HEIGHT };
